import type { InquiryV2 } from '@/services/getInquiriesV2';
import type { InquiryLabel } from '@/services/inquiryLabels';
import { getAvatarUrl } from '@/util/getAvatarUrl';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { getInquiryDisplayParticipant, getInquiryTitleExpert } from '@/util/inquiryDisplay';
import { isAskAnonymously } from '@/util/userFilters';
import { buildModRouteGroupExperts, isModRouteRecipient } from '@/utils/modHelpers';

export type InquiryGroup = {
  groupKey: string;
  parentId: number;
  inquiries: InquiryV2[];
  unreadCount: number;
};

export type InquiryGroupExpert = {
  name: string;
  avatarUrl?: string;
  /** Rendering variant for this entry on the inbox card. `route` means the
   * entry is a MOD Direct-to-MSL route label (J&J MSL / J&J Medical
   * Information Team). Default (undefined) renders as the standard
   * avatar+name expert chip. */
  variant?: 'route';
};

export type InquiryGroupStateBadgeAction = 'info' | 'muted';
export type InquiryGroupStateLabel = 'open' | 'close requested' | 'closed';
export type InquiryGroupStateCounts = Partial<Record<InquiryGroupStateLabel, number>>;

export type InquiryGroupStateBadge = {
  label: InquiryGroupStateLabel;
  action: InquiryGroupStateBadgeAction;
};

export type InquiryVisibilityContext =
  | 'inbox-open'
  | 'inbox-closed'
  | 'inbox-all'
  | 'chat-group-tabs'
  | 'chat-group-tabs-open'
  | 'chat-group-tabs-closed';

export function getGroupedChatTabVisibility(tabSelected?: string): InquiryVisibilityContext {
  if (tabSelected === 'open') {
    return 'chat-group-tabs-open';
  }

  if (tabSelected === 'closed') {
    return 'chat-group-tabs-closed';
  }

  return 'chat-group-tabs';
}

const getInquiryTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const timestamp = Date.parse(value);

  if (Number.isNaN(timestamp)) {
    return 0;
  }

  return timestamp;
};

export function sortInquiriesByLatestActivity(inquiries: InquiryV2[]): InquiryV2[] {
  return [...inquiries].sort((leftInquiry, rightInquiry) => {
    const lastActivityTimestampDiff =
      getInquiryTimestamp(rightInquiry.lastResponseDate) -
      getInquiryTimestamp(leftInquiry.lastResponseDate);

    if (lastActivityTimestampDiff !== 0) {
      return lastActivityTimestampDiff;
    }

    if (leftInquiry.id !== rightInquiry.id) {
      return leftInquiry.id - rightInquiry.id;
    }

    return leftInquiry.conversationId.localeCompare(rightInquiry.conversationId);
  });
}

const INQUIRY_STATUS_SORT_PRIORITY: Record<InquiryV2['inquiryStatus'], number> = {
  active: 0,
  initializing: 1,
  inactive: 2,
  closed: 3,
};

/**
 * Sorts grouped chat tab inquiries into a stable, status-first order across surfaces.
 */
export function sortInquiriesForGroupTabDisplay<T extends InquiryV2>(inquiries: T[]): T[] {
  return [...inquiries].sort((a, b) => {
    const statusPriorityDiff =
      INQUIRY_STATUS_SORT_PRIORITY[a.inquiryStatus] - INQUIRY_STATUS_SORT_PRIORITY[b.inquiryStatus];

    if (statusPriorityDiff !== 0) {
      return statusPriorityDiff;
    }

    if (a.id !== b.id) {
      return a.id - b.id;
    }

    return a.conversationId.localeCompare(b.conversationId);
  });
}

export function getInquiryParentKey(inquiry: InquiryV2) {
  return inquiry.parentId ?? inquiry.id;
}

export function isInquiryAuthor(inquiry: InquiryV2, userId?: string) {
  return !!userId && inquiry.userId === Number(userId);
}

function isAskAnonGroupedRecipient(
  inquiry: InquiryV2,
  userId?: string,
  isAnonymousInquiryGroupingEnabled = false
) {
  if (!userId || !inquiry.anonymous) {
    return false;
  }

  if (inquiry.recipient.id !== Number(userId)) {
    return false;
  }

  const recipientName = [inquiry.recipient.firstName, inquiry.recipient.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    isAskAnonymously(recipientName) &&
    Boolean(getInquiryTitleExpert(inquiry, isAnonymousInquiryGroupingEnabled))
  );
}

/**
 * Whether this viewer should see multi-expert inbox grouping and grouped chat tabs.
 * Authors always see grouped multi-expert inquiries. Ask Anonymously also sees grouped
 * anonymous multi-expert inquiries so the support inbox can work off the same grouped
 * container. Without userId returns true so callers without session context keep previous behavior.
 */
export function canUserViewInquiryGroup(
  inquiry: InquiryV2,
  userId?: string,
  isAnonymousInquiryGroupingEnabled = false
) {
  if (!userId) {
    return true;
  }

  return (
    isInquiryAuthor(inquiry, userId) ||
    isAskAnonGroupedRecipient(inquiry, userId, isAnonymousInquiryGroupingEnabled) ||
    isModRouteRecipient(inquiry, userId)
  );
}

export function isOpenTabInquiryForUser(inquiry: InquiryV2, userId?: string) {
  if (inquiry.inquiryStatus === 'active') {
    return true;
  }

  if (inquiry.inquiryStatus === 'inactive') {
    return isInquiryAuthor(inquiry, userId);
  }

  return false;
}

export function filterOpenTabInquiriesForUser(inquiries: InquiryV2[], userId?: string) {
  return filterInquiriesByVisibilityContext(inquiries, 'inbox-open', userId);
}

export function isClosedTabInquiryForUser(inquiry: InquiryV2, userId?: string) {
  if (inquiry.inquiryStatus === 'closed') {
    return true;
  }

  if (inquiry.inquiryStatus === 'inactive') {
    if (!userId) {
      return false;
    }

    return !isInquiryAuthor(inquiry, userId);
  }

  return false;
}

export function filterClosedTabInquiriesForUser(inquiries: InquiryV2[], userId?: string) {
  return filterInquiriesByVisibilityContext(inquiries, 'inbox-closed', userId);
}

export function isInquiryVisibleForContext(
  inquiry: InquiryV2,
  context: InquiryVisibilityContext,
  userId?: string
) {
  if (context === 'inbox-open') {
    return isOpenTabInquiryForUser(inquiry, userId);
  }

  if (context === 'inbox-closed') {
    return isClosedTabInquiryForUser(inquiry, userId);
  }

  if (context === 'chat-group-tabs') {
    return true;
  }

  if (context === 'chat-group-tabs-open') {
    return isOpenTabInquiryForUser(inquiry, userId);
  }

  if (context === 'chat-group-tabs-closed') {
    return isClosedTabInquiryForUser(inquiry, userId);
  }

  return true;
}

export function filterInquiriesByVisibilityContext<T extends InquiryV2>(
  inquiries: T[],
  context: InquiryVisibilityContext,
  userId?: string
) {
  return inquiries.filter((inquiry) => isInquiryVisibleForContext(inquiry, context, userId));
}

export function getInquiryGroupKey(inquiry: InquiryV2) {
  return `parent-${getInquiryParentKey(inquiry)}`;
}

export function getInquiryGroupKeyByConversationId(
  inquiries: InquiryV2[],
  conversationId: string
): string | undefined {
  const inquiry = inquiries.find((item) => item.conversationId === conversationId);

  return inquiry ? getInquiryGroupKey(inquiry) : undefined;
}

export function getInquiryRelatedCountMap(inquiries: InquiryV2[]) {
  const counts = new Map<string, number>();

  const groupSizes = new Map<string, number>();

  inquiries.forEach((inquiry) => {
    const key = getInquiryGroupKey(inquiry);

    groupSizes.set(key, (groupSizes.get(key) ?? 0) + 1);
  });

  inquiries.forEach((inquiry) => {
    counts.set(inquiry.conversationId, groupSizes.get(getInquiryGroupKey(inquiry)) ?? 1);
  });

  return counts;
}

export function getInquiryLabelDedupKey(label: InquiryLabel) {
  return `${label.key}:${typeof label.value === 'string' ? label.value : String(label.value)}`;
}

export function getUniqueInquiryLabels(inquiries: InquiryV2[]) {
  return Array.from(
    new Map(
      inquiries
        .flatMap((inquiry) => inquiry.labels ?? [])
        .map((label) => [getInquiryLabelDedupKey(label), label])
    ).values()
  );
}

export function getInquiryConversationIds(inquiries: InquiryV2[]) {
  return inquiries.map((inquiry) => inquiry.conversationId);
}

export function getInquiryGroupExperts(
  group: InquiryGroup,
  isAnonymousInquiryGroupingEnabled = false,
  userId?: string
): InquiryGroupExpert[] {
  const modRouteExperts = buildModRouteGroupExperts(group, userId);

  if (modRouteExperts) {
    return modRouteExperts;
  }

  return Array.from(
    new Map(
      group.inquiries
        .map((inquiry) =>
          getInquiryDisplayParticipant(
            inquiry,
            inquiry.recipient,
            isAnonymousInquiryGroupingEnabled
          )
        )
        .filter((expert) => Boolean(expert?.id))
        .map((expert) => [
          expert.id,
          {
            name: getProfileNameAndInitials({
              firstName: expert.firstName,
              lastName: expert.lastName,
              identifiesAsDoctor: expert.identifiesAsDoctor,
            }).formalName,
            avatarUrl: getAvatarUrl(expert.avatar),
          },
        ])
    ).values()
  );
}

/**
 * Buckets inquiries for inbox rows. Authors get one row per parent group. Recipients get
 * one row per conversation so they never see a merged multi-expert card.
 */
export function groupInquiries(
  inquiries: InquiryV2[],
  userId?: string,
  isAnonymousInquiryGroupingEnabled = false
): InquiryGroup[] {
  const groups = new Map<string, InquiryGroup>();

  inquiries.forEach((inquiry) => {
    const shouldGroupInquiry = canUserViewInquiryGroup(
      inquiry,
      userId,
      isAnonymousInquiryGroupingEnabled
    );

    const groupKey = shouldGroupInquiry
      ? getInquiryGroupKey(inquiry)
      : `conversation-${inquiry.conversationId}`;

    const existing = groups.get(groupKey);

    if (existing) {
      existing.inquiries.push(inquiry);

      existing.unreadCount += inquiry.hasUnread ? 1 : 0;

      return;
    }

    groups.set(groupKey, {
      groupKey,
      parentId: getInquiryParentKey(inquiry),
      inquiries: [inquiry],
      unreadCount: inquiry.hasUnread ? 1 : 0,
    });
  });

  return Array.from(groups.values());
}

/**
 * Builds inbox groups for the list. When parent grouping is on pass the current user id so
 * expert inboxes do not merge sibling threads into one card.
 */
export function buildInboxInquiryGroups(
  inquiries: InquiryV2[],
  shouldGroupByParent: boolean,
  userId?: string,
  isAnonymousInquiryGroupingEnabled = false
): InquiryGroup[] {
  const sortGroups = (groups: InquiryGroup[]) =>
    groups
      .map((group) => ({
        ...group,
        inquiries: sortInquiriesByLatestActivity(group.inquiries),
      }))
      .sort((a, b) => {
        const newestInA = getInquiryTimestamp(a.inquiries[0]?.lastResponseDate);

        const newestInB = getInquiryTimestamp(b.inquiries[0]?.lastResponseDate);

        return newestInB - newestInA;
      });

  if (!shouldGroupByParent) {
    const ungroupedInquiries = inquiries.map((inquiry) => ({
      groupKey: `conversation-${inquiry.conversationId}`,
      parentId: getInquiryParentKey(inquiry),
      inquiries: [inquiry],
      unreadCount: inquiry.hasUnread ? 1 : 0,
    }));

    return sortGroups(ungroupedInquiries);
  }

  return sortGroups(groupInquiries(inquiries, userId, isAnonymousInquiryGroupingEnabled));
}

/**
 * Inquiries to render as grouped chat tabs after open or closed tab rules. Empty for anyone
 * who is not the inquiry author.
 */
export function getVisibleInquiryGroupTabInquiries<T extends InquiryV2>(
  inquiries: T[],
  context: InquiryVisibilityContext,
  userId?: string,
  isAnonymousInquiryGroupingEnabled = false
) {
  if (!inquiries.length) {
    return [];
  }

  if (!canUserViewInquiryGroup(inquiries[0], userId, isAnonymousInquiryGroupingEnabled)) {
    return [];
  }

  return sortInquiriesForGroupTabDisplay(
    filterInquiriesByVisibilityContext(inquiries, context, userId)
  );
}

/**
 * Sibling inquiries under the same parent as the selected conversation. Empty for experts
 * when userId is passed so split inbox chat never shows other experts threads.
 */
export function getInquiryGroups(
  inquiries: InquiryV2[],
  conversationId: string,
  options?: {
    context?: InquiryVisibilityContext;
    userId?: string;
    groupKeyFallback?: string;
    isAnonymousInquiryGroupingEnabled?: boolean;
  }
) {
  const userId = options?.userId;

  const isAnonymousInquiryGroupingEnabled = options?.isAnonymousInquiryGroupingEnabled ?? false;

  const selectedInquiry = inquiries.find((item) => item.conversationId === conversationId);

  if (
    selectedInquiry &&
    userId &&
    !canUserViewInquiryGroup(selectedInquiry, userId, isAnonymousInquiryGroupingEnabled)
  ) {
    return [];
  }

  let groupKey = getInquiryGroupKeyByConversationId(inquiries, conversationId);

  if (!groupKey && selectedInquiry && !userId) {
    groupKey = getInquiryGroupKey(selectedInquiry);
  }

  if (!groupKey) {
    groupKey = options?.groupKeyFallback;
  }

  const context = options?.context;

  if (!groupKey) {
    return [];
  }

  const groupedInquiries = inquiries.filter((inquiry) => getInquiryGroupKey(inquiry) === groupKey);

  const tabInquiries = context
    ? getVisibleInquiryGroupTabInquiries(
        groupedInquiries,
        context,
        userId,
        isAnonymousInquiryGroupingEnabled
      )
    : sortInquiriesForGroupTabDisplay(groupedInquiries);

  return tabInquiries;
}

/**
 * The sibling inquiries to render as chat tabs for the open conversation.
 *
 * Prefers the group the single-inquiry endpoint returns, which the server resolves
 * in full. The inbox list is only the fallback, for a thread the endpoint reports
 * no group for. The list holds just the pages scrolled in so far, so building the
 * group from it silently drops any sibling on a page that has not loaded, and the
 * tab strip shrinks or disappears depending on how far the reader has scrolled.
 */
export function getChatGroupTabInquiries({
  serverGroupInquiries,
  listInquiries,
  conversationId,
  userId,
  groupKeyFallback,
  isAnonymousInquiryGroupingEnabled = false,
}: {
  serverGroupInquiries?: InquiryV2[];
  listInquiries: InquiryV2[];
  conversationId: string;
  userId?: string;
  groupKeyFallback?: string;
  isAnonymousInquiryGroupingEnabled?: boolean;
}) {
  if (serverGroupInquiries && serverGroupInquiries.length > 1) {
    return getVisibleInquiryGroupTabInquiries(
      serverGroupInquiries,
      'chat-group-tabs',
      userId,
      isAnonymousInquiryGroupingEnabled
    );
  }

  return getInquiryGroups(listInquiries, conversationId, {
    context: 'chat-group-tabs',
    userId,
    groupKeyFallback,
    isAnonymousInquiryGroupingEnabled,
  });
}

/**
 * Returns the conversationId that should be selected as the first tab when
 * opening an inquiry group from the inbox. This follows the most recent
 * conversation activity so grouped inquiry entry opens on the latest message.
 */
export function getFirstTabConversationId(group: InquiryGroup): string | undefined {
  return sortInquiriesByLatestActivity(group.inquiries)[0]?.conversationId;
}

export function dedupeInquiriesByConversationId(inquiries: InquiryV2[]) {
  const map = new Map<string, InquiryV2>();

  inquiries.forEach((inquiry) => {
    map.set(inquiry.conversationId, inquiry);
  });

  return Array.from(map.values());
}

const INQUIRY_STATE_BREAKDOWN_ORDER = ['active', 'inactive', 'closed'] as const;

type InquiryStateBreakdownStatus = (typeof INQUIRY_STATE_BREAKDOWN_ORDER)[number];

const INQUIRY_STATE_BREAKDOWN_LABELS: Record<InquiryStateBreakdownStatus, InquiryGroupStateLabel> =
  {
    active: 'open',
    inactive: 'close requested',
    closed: 'closed',
  };

export function getInquiryGroupStateCounts(
  inquiries: InquiryV2[]
): InquiryGroupStateCounts | undefined {
  if (!inquiries.length) {
    return undefined;
  }

  const counts = new Map<InquiryStateBreakdownStatus, number>();

  inquiries.forEach((inquiry) => {
    if (
      inquiry.inquiryStatus === 'active' ||
      inquiry.inquiryStatus === 'inactive' ||
      inquiry.inquiryStatus === 'closed'
    ) {
      counts.set(inquiry.inquiryStatus, (counts.get(inquiry.inquiryStatus) ?? 0) + 1);
    }
  });

  if (counts.size <= 1) {
    return undefined;
  }

  return INQUIRY_STATE_BREAKDOWN_ORDER.reduce<InquiryGroupStateCounts>((acc, status) => {
    const count = counts.get(status);

    if (!count) {
      return acc;
    }

    acc[INQUIRY_STATE_BREAKDOWN_LABELS[status]] = count;

    return acc;
  }, {});
}

export function getInquiryGroupCountSummary(
  visibleCount?: number,
  totalCount?: number
): string | undefined {
  if (!totalCount || totalCount <= 1) {
    return undefined;
  }

  const safeVisibleCount = visibleCount ?? totalCount;

  return `${safeVisibleCount} of ${totalCount}`;
}

export function getInquiryGroupStateBadgeAction(
  statusLabel: InquiryGroupStateLabel | string
): InquiryGroupStateBadgeAction {
  if (statusLabel === 'open') {
    return 'info';
  }

  if (statusLabel === 'close requested') {
    return 'info';
  }

  if (statusLabel === 'closed') {
    return 'muted';
  }

  return 'muted';
}

const INQUIRY_GROUP_BADGE_ORDER: InquiryGroupStateLabel[] = ['open', 'close requested', 'closed'];

export function getInquiryGroupStateBadges(
  breakdown?: InquiryGroupStateCounts
): InquiryGroupStateBadge[] {
  if (!breakdown) {
    return [];
  }

  return INQUIRY_GROUP_BADGE_ORDER.filter((statusLabel) => (breakdown[statusLabel] ?? 0) > 0).map(
    (statusLabel) => ({
      label: statusLabel,
      action: getInquiryGroupStateBadgeAction(statusLabel),
    })
  );
}

/**
 * The conversation a grouped chat opens on. Keeps the requested conversation when it is in
 * the group, so a link to one sibling lands on that sibling's tab, otherwise the first tab.
 *
 * The group comes from the server first, like the tab strip. The inbox list holds only the
 * pages scrolled in so far, so a group built from it can lack the requested sibling while
 * holding others, and the default then fell to whichever of those sorted first.
 */
export function getGroupedDefaultConversationId({
  serverGroupInquiries,
  listInquiries,
  conversationId,
  isGroupedEnabled,
  userId,
  groupKeyFallback,
  isAnonymousInquiryGroupingEnabled = false,
}: {
  serverGroupInquiries?: InquiryV2[];
  listInquiries: InquiryV2[];
  conversationId: string;
  isGroupedEnabled: boolean;
  userId: string;
  groupKeyFallback?: string;
  isAnonymousInquiryGroupingEnabled?: boolean;
}) {
  if (!isGroupedEnabled) {
    return conversationId;
  }

  const groupedInquiries = getChatGroupTabInquiries({
    serverGroupInquiries,
    listInquiries,
    conversationId,
    userId,
    groupKeyFallback,
    isAnonymousInquiryGroupingEnabled,
  });

  if (!groupedInquiries.length) {
    return conversationId;
  }

  const hasSelectedConversation = groupedInquiries.some(
    (groupedInquiry) => groupedInquiry.conversationId === conversationId
  );

  if (hasSelectedConversation) {
    return conversationId;
  }

  return groupedInquiries[0]?.conversationId ?? conversationId;
}

/**
 * Where a "Linked from" link should land. A forwarded group gives every expert's thread the same
 * single reference, the source conversation that was forwarded, so following it always opened the
 * source group on its first tab. The source group has one thread per expert, tagged with that
 * expert in its title, so the link goes to the one for the expert this thread is addressed to.
 * Anything unresolved keeps the original reference, which is what the link did before.
 */
export function getLinkedSourceConversationId({
  referenceConversationId,
  referencedGroupInquiries,
  recipientId,
}: {
  referenceConversationId: string;
  referencedGroupInquiries?: readonly Pick<InquiryV2, 'conversationId' | 'title'>[] | null;
  recipientId?: number | string | null;
}) {
  if (recipientId === null || recipientId === undefined) {
    return referenceConversationId;
  }

  const matchingSource = referencedGroupInquiries?.find(
    (groupInquiry) => String(groupInquiry.title?.expert?.id) === String(recipientId)
  );

  return matchingSource?.conversationId ?? referenceConversationId;
}

/**
 * A chat tab's name for the person on the other side: "Dr. Last" for someone who identifies as a
 * doctor and has a last name, otherwise their full name. One rule for every tab strip, so a send's
 * tabs and an inquiry group card's tabs name an expert the same way.
 */
export function getGroupTabLabel({
  firstName,
  lastName,
  identifiesAsDoctor,
}: {
  firstName?: string | null;
  lastName?: string | null;
  identifiesAsDoctor?: boolean | null;
}) {
  const trimmedLastName = lastName?.trim();

  if (identifiesAsDoctor && trimmedLastName) {
    return `Dr. ${trimmedLastName}`;
  }

  return getProfileNameAndInitials({
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    identifiesAsDoctor: Boolean(identifiesAsDoctor),
  }).name;
}

/** The experts staff relayed a submitter's thread to, as the inquiry detail route names them. */
export type ThreadRelays = readonly {
  expert: { firstName: string | null; lastName: string | null; identifiesAsDoctor: boolean };
}[];

/**
 * A tab's title for one of a submitter's threads, as Ask Anonymously sees them.
 *
 * A thread that picked an expert is "For Dr. X". One that picked nobody is addressed only to the
 * routing account, so "For Ask Anonymously" would name every such tab alike: separate submissions
 * auto-grouped under one question became a strip of identical tabs. It is named instead for the
 * experts staff relayed it to, "For Dr. Gao" or "For Dr. Gao +2", and "Not sent yet" before any relay.
 * The relays come from the server, which reads each relay's link back to its source thread.
 */
export function getRelayViewTabTitle(
  relayedTo: ThreadRelays | undefined,
  participantLabel: string
) {
  // Relays unknown (a tab from the inbox list, or the detail still loading) is not the same as
  // none: saying "Not sent yet" there would be wrong for a thread already relayed.
  if (!isAskAnonymously(participantLabel) || relayedTo === undefined) {
    return `For ${participantLabel}`;
  }

  const [first, ...rest] = relayedTo;

  if (!first) {
    return 'Not sent yet';
  }

  const name = getGroupTabLabel(first.expert);

  return rest.length > 0 ? `For ${name} +${rest.length}` : `For ${name}`;
}
